export type ChatScrollState = {
  scrollTop: number;
  isAtBottom: boolean;
};

export const CHAT_SCROLL_CONTAINER_SELECTOR =
  '[data-chat-scroll-container="true"]';

const scrollStateKey = (conversationId: string): string =>
  `chat:scrollPosition:${conversationId}`;

export const computeMaxScrollTop = (element: HTMLElement): number =>
  Math.max(0, element.scrollHeight - element.clientHeight);

export const computeIsAtBottom = (element: HTMLElement): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return Math.abs(scrollHeight - scrollTop - clientHeight) <= 1;
};

/** When the user was at the bottom, scroll to current bottom — not a stale scrollTop. */
export const resolveRestoredScrollTop = (
  saved: ChatScrollState,
  container: HTMLElement
): number =>
  saved.isAtBottom ? computeMaxScrollTop(container) : saved.scrollTop;

export const saveChatScrollState = (
  conversationId: string,
  container: HTMLElement
): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  const state: ChatScrollState = {
    scrollTop: container.scrollTop,
    isAtBottom: computeIsAtBottom(container),
  };
  sessionStorage.setItem(scrollStateKey(conversationId), JSON.stringify(state));
};

export const saveChatScrollStateFromDom = (conversationId: string): void => {
  const container = document.querySelector(
    CHAT_SCROLL_CONTAINER_SELECTOR
  ) as HTMLElement | null;

  if (container) {
    saveChatScrollState(conversationId, container);
  }
};

export const getChatScrollState = (
  conversationId: string
): ChatScrollState | null => {
  if (typeof sessionStorage === "undefined") {
    return null;
  }

  const raw = sessionStorage.getItem(scrollStateKey(conversationId));
  if (!raw) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "scrollTop" in parsed &&
      "isAtBottom" in parsed &&
      typeof (parsed as ChatScrollState).scrollTop === "number" &&
      typeof (parsed as ChatScrollState).isAtBottom === "boolean"
    ) {
      return parsed as ChatScrollState;
    }
    if (typeof parsed === "number" && !Number.isNaN(parsed)) {
      return { scrollTop: parsed, isAtBottom: false };
    }
  } catch {
    const legacyScrollTop = Number.parseInt(raw, 10);
    if (!Number.isNaN(legacyScrollTop)) {
      return { scrollTop: legacyScrollTop, isAtBottom: false };
    }
  }

  return null;
};

export const clearChatScrollState = (conversationId: string): void => {
  if (typeof sessionStorage === "undefined") {
    return;
  }

  sessionStorage.removeItem(scrollStateKey(conversationId));
};
